<template>
  <div class="final-decision" :class="verdictToneClass">
    <div class="final-decision__header">
      <h2 class="final-decision__title">{{ cardTitle }}</h2>
      <p v-if="cardSubtitle" class="final-decision__subtitle">{{ cardSubtitle }}</p>
    </div>

    <div v-if="finalDecision" class="final-decision__current">
      <div class="final-decision__entry-head">
        <span class="final-decision__status-icon" :class="statusIconClass(displayVote)">
          <BaseIcon :name="statusIconName(displayVote)" />
        </span>
        <div class="final-decision__entry-identity">
          <span class="final-decision__entry-name-row">
            <span class="final-decision__entry-name" v-tooltip="emailTooltip(finalDecision.decidedBy)">
              {{ userFullName(finalDecision.decidedBy) }}
            </span>
            <BaseTag v-if="isMe(finalDecision.decidedBy)" preset="secondary" tone="neutral">
              {{ $t('shared.me') }}
            </BaseTag>
          </span>
          <span class="final-decision__entry-date">{{ decidedAtLabel }}</span>
        </div>
        <BaseTag class="final-decision__badge" preset="soft" :tone="getGoNoGoVoteTone(displayVote)">
          {{ voteLabel(displayVote) }}
        </BaseTag>
      </div>

      <div v-if="finalDecision.reasons && finalDecision.reasons.length" class="final-decision__reasons">
        <BaseChip
          v-for="(reason, index) in finalDecision.reasons"
          :key="`reason-${index}`"
          size="sm"
          preset="soft"
          :tone="getGoNoGoVoteTone(displayVote)"
          :interactive="false"
        >
          {{ reason }}
        </BaseChip>
      </div>

      <p v-if="finalDecision.comment" class="final-decision__comment">
        {{ normalizeVoteReasonNewlines(finalDecision.comment) }}
      </p>
    </div>

    <div v-if="pastEntries.length" class="final-decision__journal">
      <button
        type="button"
        class="final-decision__journal-toggle"
        :aria-expanded="String(journalExpanded)"
        aria-controls="final-decision-journal"
        @click="journalExpanded = !journalExpanded"
      >
        {{ $t('go-nogo.final-decision.journal-title', { count: pastEntries.length }) }}
        <BaseIcon :name="journalExpanded ? 'chevron-up' : 'chevron-down'" />
      </button>

      <div v-if="journalExpanded" id="final-decision-journal" class="final-decision__journal-entries">
        <div v-for="entry in pastEntries" :key="entry.id" class="final-decision__journal-entry">
          <div class="final-decision__entry-head">
            <span class="final-decision__status-icon" :class="statusIconClass(entryDisplayVote(entry))">
              <BaseIcon :name="entryIconName(entry)" />
            </span>
            <div class="final-decision__entry-identity">
              <span class="final-decision__entry-name-row">
                <span class="final-decision__entry-name" v-tooltip="emailTooltip(entry.decidedBy)">
                  {{ userFullName(entry.decidedBy) }}
                </span>
                <BaseTag v-if="isMe(entry.decidedBy)" preset="secondary" tone="neutral">
                  {{ $t('shared.me') }}
                </BaseTag>
              </span>
              <span class="final-decision__entry-date">{{ entryDateLabel(entry) }}</span>
            </div>
            <BaseTag v-if="isDeletion(entry)" class="final-decision__badge" preset="soft" tone="neutral">
              {{ $t('go-nogo.final-decision.deleted') }}
            </BaseTag>
            <BaseTag
              v-else
              class="final-decision__badge"
              preset="soft"
              :tone="getGoNoGoVoteTone(entryDisplayVote(entry))"
            >
              {{ voteLabel(entryDisplayVote(entry)) }}
            </BaseTag>
          </div>

          <div v-if="entry.reasons && entry.reasons.length" class="final-decision__reasons">
            <BaseChip
              v-for="(reason, index) in entry.reasons"
              :key="`entry-reason-${index}`"
              size="sm"
              preset="soft"
              :tone="getGoNoGoVoteTone(entryDisplayVote(entry))"
              :interactive="false"
            >
              {{ reason }}
            </BaseChip>
          </div>

          <p v-if="entry.comment" class="final-decision__comment">
            {{ normalizeVoteReasonNewlines(entry.comment) }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="canRecord" class="final-decision__actions">
      <BaseVoteButton @click.native="$emit('record')">
        {{ $t('go-nogo.final-decision.decide') }}
      </BaseVoteButton>
    </div>
    <div v-else-if="isOwner" class="final-decision__actions">
      <BaseVoteButton @click.native="$emit('change')">
        {{ $t('go-nogo.final-decision.change') }}
      </BaseVoteButton>
      <BaseVoteButton @click.native="$emit('delete')">
        {{ $t('go-nogo.final-decision.delete') }}
      </BaseVoteButton>
    </div>
  </div>
</template>

<script>
import BaseChip from '@/components/base/BaseChip.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseVoteButton from '@/components/base/BaseVoteButton.vue';

import { formatLongDate } from '@/util/date-util';
import { normalizeVoteReasonNewlines } from '@/util/go-nogo-vote-reasons';
import { FINAL_DECISION_EVENT, isFinalDecisionOwner, toFinalDecisionDisplayVote } from '@/util/go-nogo-final-decision';
import { getGoNoGoVoteTone } from '@/util/tag-tones/go-nogo-tag';
import { mapGetters } from 'vuex';

export default {
  name: 'FinalDecisionCard',
  components: {
    BaseChip,
    BaseIcon,
    BaseTag,
    BaseVoteButton,
  },
  data() {
    return {
      journalExpanded: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser', 'canDecideGoNoGo']),
    ...mapGetters('goNoGo', ['finalDecision', 'finalDecisionHistory']),
    ...mapGetters('tenant', ['users']),
    displayVote() {
      return this.finalDecision && toFinalDecisionDisplayVote(this.finalDecision.vote);
    },
    // Keyed on capability, not just state: "Prendre la décision finale" is an instruction, so it
    // must not be shown to someone who can't act.
    cardTitle() {
      if (!this.finalDecision && this.canRecord) return this.$t('go-nogo.final-decision.section-title');
      return this.$t('go-nogo.final-decision.decided-title');
    },
    cardSubtitle() {
      if (this.finalDecision) return null;
      return this.canRecord
        ? this.$t('go-nogo.final-decision.decide-subtitle')
        : this.$t('go-nogo.final-decision.awaiting-subtitle');
    },
    verdictToneClass() {
      return this.finalDecision ? `final-decision--${getGoNoGoVoteTone(this.displayVote)}` : null;
    },
    // `updatedAt` is null until the first change, so it doubles as the "recorded vs changed" signal.
    decidedAtLabel() {
      if (!this.finalDecision) return '';
      const changed = !!this.finalDecision.updatedAt;
      const date = this.longDate(changed ? this.finalDecision.updatedAt : this.finalDecision.createdAt);
      return this.$t(changed ? 'go-nogo.final-decision.changed-on' : 'go-nogo.final-decision.decided-on', { date });
    },
    isOwner() {
      return isFinalDecisionOwner(this.finalDecision, this.currentUser);
    },
    // First-come-first-served: while nothing is recorded, any permission holder may decide.
    canRecord() {
      return !this.finalDecision && this.canDecideGoNoGo;
    },
    // A live decision's newest VOTE event is the one rendered above, so it is dropped here. With no
    // decision the newest event is a DELETE and every entry is genuinely past.
    pastEntries() {
      const history = this.finalDecisionHistory || [];
      if (this.finalDecision && history.length && history[0].eventType === FINAL_DECISION_EVENT.VOTE) {
        return history.slice(1);
      }
      return history;
    },
  },
  methods: {
    getGoNoGoVoteTone,
    normalizeVoteReasonNewlines,
    longDate(date) {
      return formatLongDate(date);
    },
    decisionMaker(userId) {
      return (this.users || []).find((candidate) => candidate.id === userId) || null;
    },
    userFullName(userId) {
      const user = this.decisionMaker(userId);
      return user ? `${user.firstName} ${user.lastName}` : this.$t('unknown.user');
    },
    isMe(userId) {
      return !!this.currentUser && userId === this.currentUser.id;
    },
    // Empty content makes `v-tooltip` a no-op, covering an unresolved decision maker.
    emailTooltip(userId) {
      const user = this.decisionMaker(userId);
      return { content: user ? user.email : '', placement: 'top', offset: '8px' };
    },
    voteLabel(displayVote) {
      return displayVote ? this.$t(`go-nogo.${displayVote.toLowerCase()}`) : '';
    },
    isDeletion(entry) {
      return entry.eventType === FINAL_DECISION_EVENT.DELETE;
    },
    entryDisplayVote(entry) {
      return toFinalDecisionDisplayVote(entry.vote);
    },
    entryDateLabel(entry) {
      const date = this.longDate(entry.createdAt);
      return this.isDeletion(entry)
        ? this.$t('go-nogo.final-decision.deleted-on', { date })
        : this.$t('go-nogo.final-decision.previous-on', { date });
    },
    // Bare glyphs, not the `*-circle` variants: those draw their own ring inside the coloured disc,
    // and Feather's check-circle overshoots its arc so the check reads as off-centre when nested.
    statusIconName(displayVote) {
      return displayVote === 'NOGO' ? 'x' : 'check';
    },
    entryIconName(entry) {
      return this.isDeletion(entry) ? 'trash-2' : this.statusIconName(this.entryDisplayVote(entry));
    },
    statusIconClass(displayVote) {
      return `final-decision__status-icon--${getGoNoGoVoteTone(displayVote)}`;
    },
  },
};
</script>

<style lang="scss">
// Centre line of the status disc within a journal entry: where the two rail segments meet, so the
// junction stays hidden behind the disc.
$timeline-junction: 18px;

.final-decision {
  background-color: $color-white;
  border-radius: $border-radius-root;
  box-shadow: $shadow-default;
  // Reserved as transparent even with no decision, so recording one doesn't shift the card by 4px.
  border-top: 4px solid transparent;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &--success {
    border-top-color: $color-green;
  }

  &--destructive {
    border-top-color: $color-error;
  }

  &--neutral {
    border-top-color: $color-neutral;
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  &__title {
    margin: 0;
    font-size: $font-size-xbigger;
    line-height: 24px;
    font-weight: 900;
    color: $color-normal;
    text-align: left;
  }

  &__subtitle {
    margin: 0;
    font-size: $font-size-small;
    line-height: 1.5;
    color: $color-normal-pale;
  }

  &__current {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__entry-head {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__entry-identity {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    margin-right: auto;
  }

  &__entry-name-row {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  &__entry-name {
    font-size: $font-size-small;
    font-weight: $font-weight-bold;
    font-family: $font-family-raleway;
    color: $color-normal;
    line-height: 1.3;
  }

  &__entry-date {
    font-size: $font-size-xsmall;
    color: $color-neutral;
    line-height: 1.3;
  }

  // Must mask the rail behind it. `position: relative` alone isn't enough: with `z-index: auto`,
  // positioned boxes paint in tree order, so the entry's `::after` would strike through the glyph.
  &__status-icon {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border-radius: $border-radius-round;
    background-color: $color-neutral;

    .base-icon {
      width: 15px;
      height: 15px;
      color: $color-white;
    }

    &--success {
      background-color: $color-green;
    }

    &--destructive {
      background-color: $color-error;
    }
  }

  &__badge {
    flex-shrink: 0;
    text-transform: uppercase;
  }

  &__reasons {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding-left: 36px;
  }

  &__comment {
    margin: 0;
    margin-left: 36px;
    font-size: $font-size-small;
    font-style: italic;
    line-height: 1.5;
    color: $color-normal-pale;
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__journal {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  // A border on the header instead would double up with `__actions` when there is no journal.
  &__current + &__journal,
  &__header + &__journal {
    border-top: 1px solid $color-border;
    padding-top: 16px;
  }

  &__journal-toggle {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 4px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: $font-size-xsmall;
    font-weight: $font-weight-bold;
    color: $color-normal-pale;

    .base-icon {
      width: 14px;
      height: 14px;
    }

    &:hover {
      color: $color-normal;
    }

    &:focus-visible {
      outline: 2px solid $color-accent-500;
      outline-offset: 2px;
      border-radius: $border-radius-small;
    }
  }

  &__journal-entries {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  // Two segments meeting at $timeline-junction: continuous at any entry height, while the first
  // entry shows no stub above its disc and the last still receives the line from above.
  &__journal-entry {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;

    &:not(:first-child)::before,
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      left: 11px;
      width: 2px;
      background-color: $color-border;
    }

    &:not(:first-child)::before {
      top: 0;
      height: $timeline-junction;
    }

    &:not(:last-child)::after {
      top: $timeline-junction;
      bottom: -16px;
    }
  }

  &__actions {
    display: flex;
    gap: 12px;
    border-top: 1px solid $color-border;
    padding-top: 20px;
  }

  // The journal already carries a rule above it; a second one below would box it in.
  &__journal + &__actions {
    border-top: 0;
    padding-top: 0;
  }

  @media screen and (max-width: 620px) {
    padding: 24px 20px;
  }
}
</style>
